import React from 'react';

export default function PortManagement({
  selectedTile,
  userId,
  shippingRoutes,
  toggleShippingRoute,
  isPortConnected,
  getOwnedPorts,
  getOwnedOilRigs,
  routeLimit,
  getTileUpgradeLevel,
  getMaxRoutes,
  getTradePartnerPorts, // New prop to get trade partner's ports
  getTradePartnerOilRigs, // New prop to get trade partner's oil rigs
  getEquipmentUpgradeLevel,
  getEquipmentMultiplier
}) {
  if (!selectedTile || selectedTile.type !== 7 || !selectedTile.owner || selectedTile.owner.owner !== userId) {
    return null;
  }

  const upgradeLevel = getTileUpgradeLevel ? getTileUpgradeLevel(selectedTile.key) : 0;
  const equipmentLevel = getEquipmentUpgradeLevel ? getEquipmentUpgradeLevel(selectedTile.key) : 0;
  const equipmentMultiplier = getEquipmentMultiplier ? getEquipmentMultiplier(equipmentLevel) : 1.0;
  const maxRoutes = getMaxRoutes ? getMaxRoutes(upgradeLevel) : routeLimit;
  const currentConnections = (shippingRoutes[selectedTile.key] || []).length;
  const isAtLimit = currentConnections >= maxRoutes;

  // Ensure we get fresh trade partner properties each render
  const ownedPorts = getOwnedPorts().filter(port => port.key !== selectedTile.key);
  const tradePartnerPorts = getTradePartnerPorts ? getTradePartnerPorts().filter(port => port.key !== selectedTile.key) : [];
  const allAvailablePorts = [...ownedPorts, ...tradePartnerPorts];

  const ownedOilRigs = getOwnedOilRigs();
  const tradePartnerOilRigs = getTradePartnerOilRigs ? getTradePartnerOilRigs() : [];
  const allAvailableOilRigs = [...ownedOilRigs, ...tradePartnerOilRigs];

  const getEquipmentBenefitText = () => {
    if (equipmentLevel === 0) return null;
    const percentage = ((equipmentMultiplier - 1) * 100).toFixed(0);
    return `+${percentage}% income & costs (Equipment Lv${equipmentLevel})`;
  };

  return (
    <div style={{
      marginBottom: '15px',
      padding: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '5px',
      border: '1px solid rgba(255, 255, 255, 0.3)'
    }}>
      <h5 style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: '#E8F5E8', wordWrap: 'break-word' }}>
        🚢 Port Shipping Route Connections
      </h5>
      
      {(allAvailablePorts.length === 0 && allAvailableOilRigs.length === 0) ? (
        <div style={{ 
          fontSize: '0.75em', 
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center',
          fontStyle: 'italic',
          wordWrap: 'break-word'
        }}>
          You need to own other ports or oil rigs, or have a trade agreement to create shipping routes
        </div>
      ) : (
        <div>
          <div style={{ fontSize: '0.75em', color: '#C8E6C9', marginBottom: '8px', wordWrap: 'break-word' }}>
            Select ports and oil rigs to connect with:
          </div>
          
          {/* Show other ports */}
          {allAvailablePorts.map(port => {
            const isConnected = isPortConnected(selectedTile.key, port.key);
            const isDisabled = !isConnected && isAtLimit;
            const isTradePartner = !ownedPorts.some(p => p.key === port.key);
            
            return (
              <div key={port.key} style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '5px',
                fontSize: '0.75em',
                opacity: isDisabled ? 0.5 : 1
              }}>
                <input
                  type="checkbox"
                  id={`route-${port.key}`}
                  checked={isConnected}
                  disabled={isDisabled}
                  onChange={() => toggleShippingRoute(selectedTile.key, port.key)}
                  style={{
                    marginRight: '8px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                />
                <label 
                  htmlFor={`route-${port.key}`}
                  style={{ 
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    color: isConnected ? '#90EE90' : (isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'white'),
                    wordWrap: 'break-word',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ flex: 1 }}>
                    {port.name}
                    {isConnected && ' ✓'}
                  </span>
                  {isTradePartner && (
                    <span style={{ 
                      fontSize: '0.7em', 
                      color: '#9C27B0', 
                      marginLeft: '4px',
                      fontWeight: 'bold'
                    }}>
                      🤝
                    </span>
                  )}
                </label>
              </div>
            );
          })}
          
          {/* Show oil rigs */}
          {allAvailableOilRigs.map(oilRig => {
            const isConnected = isPortConnected(selectedTile.key, oilRig.key);
            const isDisabled = !isConnected && isAtLimit;
            const isTradePartner = !ownedOilRigs.some(o => o.key === oilRig.key);
            
            return (
              <div key={oilRig.key} style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '5px',
                fontSize: '0.75em',
                opacity: isDisabled ? 0.5 : 1
              }}>
                <input
                  type="checkbox"
                  id={`route-${oilRig.key}`}
                  checked={isConnected}
                  disabled={isDisabled}
                  onChange={() => toggleShippingRoute(selectedTile.key, oilRig.key)}
                  style={{
                    marginRight: '8px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                />
                <label 
                  htmlFor={`route-${oilRig.key}`}
                  style={{ 
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    color: isConnected ? '#90EE90' : (isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'white'),
                    wordWrap: 'break-word',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ flex: 1 }}>
                    {oilRig.name}
                    {isConnected && ' ✓'}
                  </span>
                  {isTradePartner && (
                    <span style={{ 
                      fontSize: '0.7em', 
                      color: '#9C27B0', 
                      marginLeft: '4px',
                      fontWeight: 'bold'
                    }}>
                      🤝
                    </span>
                  )}
                </label>
              </div>
            );
          })}
          
          <div style={{
            marginTop: '10px',
            fontSize: '0.7em',
            color: 'rgba(255, 255, 255, 0.6)',
            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            paddingTop: '5px',
            wordWrap: 'break-word'
          }}>
            Connected routes: {currentConnections}/{maxRoutes}
            {upgradeLevel > 0 && (
              <div style={{ color: '#90EE90', fontSize: '0.65em', marginTop: '2px' }}>
                Upgraded (Level {upgradeLevel})
              </div>
            )}
            {equipmentLevel > 0 && (
              <div style={{ color: '#FFD700', fontSize: '0.65em', marginTop: '2px' }}>
                {getEquipmentBenefitText()}
              </div>
            )}
            {isAtLimit && (
              <div style={{ color: '#FFB74D', fontSize: '0.65em', marginTop: '2px' }}>
                Maximum connections reached
              </div>
            )}
            {(tradePartnerPorts.length > 0 || tradePartnerOilRigs.length > 0) && (
              <div style={{ color: '#9C27B0', fontSize: '0.65em', marginTop: '2px' }}>
                🤝 = Trade partner's property
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}